/**
 * Tasks API (package: app/data/tasks/tasksApi.ts)
 * This file contains functions to interact with the Tasks API endpoints.
 * See documentation at the bottom of the file.
 */

import { useAxiosClient } from 'app/data/api'
import { useTimezone } from 'app/data/user/useUser'
import { convertFromUTC, mergeDateAndTime, splitDateAndTime } from 'app/utils/dates/conversions'

import { CreateTaskData, Step, StepFormData, Task, UpdateTaskData } from '../types/tasks'

interface FetchOptions {
  include?: string[]
  status?: 'incomplete' | 'completed'
  focus_at_start?: string | null
  focus_at_end?: string | null
  due_date_start?: string | null
  due_date_end?: string | null
  include_deleted?: boolean
  sort?: string
}

interface TasksResponse {
  items: Task[]
  total: number
  current_page?: string
  current_page_backwards?: string
  previous_page?: string
  next_page?: string
}

// Custom hook to interact w the tasks API
export const useTasksApi = () => {
  const axiosClient = useAxiosClient()
  const { timezone } = useTimezone()

  /**
   * Fetch tasks with optional filtering, sorting, and cursor-based pagination
   *
   * Options include:
   * - include: string[] - Include related data (e.g., 'steps')
   * - status: 'incomplete' | 'completed' - Filter tasks by status
   * - focus_at_start: string | null - Filter tasks with focus_at date starting from this date
   * - focus_at_end: string | null - Filter tasks with focus_at date up to this date
   * - due_date_start: string | null - Filter tasks with due_date starting from this date
   * - due_date_end: string | null - Filter tasks with due_date up to this date
   * - include_deleted: boolean - Include soft-deleted tasks in the results
   * - sort: string - Comma-separated list of fields to sort by. Prefix with '-' for descending order (e.g., 'position,-due_date')
   *
   * @param projectId - The ID of the project to fetch tasks for (optional)
   * @param cursor - The cursor for pagination (optional)
   * @param size - The number of tasks per page (default: 20)
   * @param options - Optional parameters for fetching tasks
   * @returns A promise that resolves to a TasksResponse object
   */
  const fetchTasks = async (
    projectId?: string,
    cursor?: string,
    size: number = 100,
    options: FetchOptions = {}
  ): Promise<TasksResponse> => {
    if (!timezone) {
      throw new Error('User timezone not available')
    }

    const params: Record<any, any> = {
      size: size.toString(),
    }

    if (cursor) params.cursor = cursor
    if (options.include) params.include = options.include.join(',')
    if (options.status) params.status = options.status
    if (options.focus_at_start) params.focus_at_start = options.focus_at_start
    if (options.focus_at_end) params.focus_at_end = options.focus_at_end
    if (options.due_date_start) params.due_date_start = options.due_date_start
    if (options.due_date_end) params.due_date_end = options.due_date_end
    if (options.include_deleted !== undefined)
      params.include_deleted = options.include_deleted.toString()
    if (options.sort) params.sort = options.sort

    const searchParams = new URLSearchParams(params)

    //console.log('fetchTasks: Fetching tasks with params:', searchParams.toString())

    const endpoint = projectId ? `/projects/${projectId}/tasks/` : `/tasks/`
    const { data } = await axiosClient.get<TasksResponse>(`${endpoint}?${searchParams.toString()}`)

    // Convert UTC dates to user's timezone for all relevant fields
    const convertedData = {
      ...data,
      items: data.items.map((task) => {
        const convertedTask = { ...task }

        convertedTask.focus_date = task.focus_at_date

        if (task.focus_at) {
          convertedTask.focus_at = convertFromUTC(task.focus_at, timezone)
          const { time: focus_time } = splitDateAndTime(convertedTask.focus_at)
          convertedTask.focus_time = focus_time
        }

        convertedTask.focus_date = task.focus_at_date
        convertedTask.created_at = convertFromUTC(task.created_at, timezone)
        convertedTask.updated_at = convertFromUTC(task.updated_at, timezone)

        // Add conversion for completed_at
        if (task.completed_at) {
          convertedTask.completed_at = convertFromUTC(task.completed_at, timezone)
        }

        return convertedTask
      }),
    }

    //console.log('fetchTasks (tz adjusted):', convertedData);

    return convertedData
  }

  /**
   * Fetch a single task by ID with optional related data
   *
   * @param taskId - The ID of the task to fetch
   * @param include - Optional array of related data to include (e.g., ['steps'])
   * @returns A promise that resolves to a Task object
   */
  const fetchTask = async (taskId: string, include?: string[]): Promise<Task> => {
    if (!timezone) {
      throw new Error('User timezone not available')
    }

    const params: Record<string, string> = {}
    if (include && include.length > 0) {
      params.include = include.join(',')
    }

    const searchParams = new URLSearchParams(params)
    const queryString = searchParams.toString()
    const endpoint = `/tasks/${taskId}${queryString ? `?${queryString}` : ''}`

    const { data } = await axiosClient.get<Task>(endpoint)

    // Convert UTC dates to user's timezone for all relevant fields
    const convertedTask = { ...data }

    convertedTask.focus_date = data.focus_at_date

    if (data.focus_at) {
      convertedTask.focus_at = convertFromUTC(data.focus_at, timezone)
      const { time: focus_time } = splitDateAndTime(convertedTask.focus_at)
      convertedTask.focus_time = focus_time
    }

    convertedTask.focus_date = data.focus_at_date
    convertedTask.created_at = convertFromUTC(data.created_at, timezone)
    convertedTask.updated_at = convertFromUTC(data.updated_at, timezone)

    // Add conversion for completed_at
    if (data.completed_at) {
      convertedTask.completed_at = convertFromUTC(data.completed_at, timezone)
    }

    return convertedTask
  }

  /**
   * Format a date string to YYYY-MM-DD format, removing any time component
   *
   * @param dateString - The date string to format
   * @returns A date string in YYYY-MM-DD format or null if input is null
   */
  const formatDateToYYYYMMDD = (dateString: string | null): string | null => {
    if (!dateString) return null
    // Extract just the date part (YYYY-MM-DD) from the string
    return dateString.split('T')[0]
  }

  /**
   * Create a new task
   *
   * @param taskData - The data for the new task
   * @returns A promise that resolves to the created Task object
   */
  const createTask = async (taskData: CreateTaskData): Promise<Task> => {
    const { focus_date, focus_time, ...restData } = taskData

    if (!timezone) {
      throw new Error('User timezone not available')
    }

    //console.log('POST Task: taskData', taskData)

    const focus_at_date = focus_date ? formatDateToYYYYMMDD(focus_date) : null
    const focus_at =
      focus_date && focus_time ? mergeDateAndTime(focus_date, focus_time, timezone) : null

    // Guard against empty string focus_at
    const finalFocusAt = focus_at === '' ? null : focus_at

    //console.log('POST Task: focus_at', finalFocusAt)
    const dataToSend = {
      ...restData,
      focus_at: finalFocusAt,
      focus_at_date,
    }

    //console.log('POST Task: taskData', dataToSend)

    try {
      const { data } = await axiosClient.post<Task>('/tasks/', dataToSend)
      return { ...data, focus_date, focus_time }
    } catch (error) {
      console.error('Error creating task:', error)
      throw error
    }
  }

  /**
   * Update an existing task
   *
   * @param taskId - The ID of the task to update
   * @param taskData - The data to update the task with
   * @returns A promise that resolves to the updated Task object
   */
  const updateTask = async (taskId: string, taskData: UpdateTaskData): Promise<Task> => {
    const { focus_date, focus_time, ...restData } = taskData

    //console.log('PUT Task: taskData', taskData)

    if (!timezone) {
      throw new Error('User timezone not available')
    }

    // Declare variables with proper types
    let focus_at: string | null = null
    let focus_at_date: string | null = null

    if (focus_date !== undefined) {
      focus_at_date = focus_date ? formatDateToYYYYMMDD(focus_date) : null
      if (focus_date && focus_time) {
        focus_at = mergeDateAndTime(focus_date, focus_time, timezone)
      }
    }

    // Guard against empty string focus_at
    const finalFocusAt = focus_at === '' ? null : focus_at

    //console.log('PUT Task: dataToSend', { ...restData, focus_at: finalFocusAt, focus_at_date })

    try {
      const { data } = await axiosClient.put<Task>(`/tasks/${taskId}`, {
        ...restData,
        focus_at: finalFocusAt,
        focus_at_date,
      })
      return { ...data, focus_date, focus_time }
    } catch (error) {
      console.error('Error updating task:', error)
      throw error
    }
  }

  /**
   * Delete a task
   * @param taskId - The ID of the task to delete
   * @returns A promise that resolves when the task is deleted
   */
  const deleteTask = async (taskId: string): Promise<void> => {
    //console.log(`deleteTask: Deleting task ${taskId}`)
    try {
      await axiosClient.delete(`/tasks/${taskId}`)
      //console.log(`deleteTask: Task ${taskId} deleted successfully`)
    } catch (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  }

  // Step operations

  /**
   * Fetch steps for a specific task
   * @param taskId - The ID of the task to fetch steps for
   * @returns A promise that resolves to an array of TaskStep objects
   */
  const getSteps = async (taskId: string): Promise<Step[]> => {
    //console.log(`getSteps: Fetching steps for task ${taskId}`)
    try {
      const { data } = await axiosClient.get<Step[]>(`/tasks/${taskId}/steps/`, {
        params: { _sort: 'position', _order: 'asc' },
      })
      //console.log(`getSteps: Fetched ${data.length} steps`)
      return data
    } catch (error) {
      console.error('Error fetching steps:', error)
      throw error
    }
  }

  /**
   * Create a new step for a task
   * @param taskId - The ID of the task to create the step for
   * @param stepData - The data for the new step (excluding the ID)
   * @returns A promise that resolves to the created TaskStep object
   */
  const createStep = async (taskId: string, stepData: StepFormData): Promise<Step> => {
    //console.log(`createStep: Creating new step for task ${taskId}`, stepData)
    try {
      const { data } = await axiosClient.post<Step>(`/tasks/${taskId}/steps`, stepData)
      //console.log('createStep: Step created successfully', data)
      return data
    } catch (error) {
      console.error('Error creating step:', error)
      throw error
    }
  }

  /**
   * Update an existing step
   * @param taskId - The ID of the task the step belongs to
   * @param stepId - The ID of the step to update
   * @param stepData - The updated step data
   * @returns A promise that resolves to the updated TaskStep object
   */
  const updateStep = async (
    taskId: string,
    stepId: string,
    stepData: Partial<StepFormData>
  ): Promise<Step> => {
    //console.log(`updateStep: Updating step ${stepId} in task ${taskId}`, stepData)
    try {
      const { data } = await axiosClient.put<Step>(`/tasks/${taskId}/steps/${stepId}`, stepData)
      //console.log('updateStep: Step updated successfully', data)
      return data
    } catch (error) {
      console.error('Error updating step:', error)
      throw error
    }
  }

  /**
   * Delete a step
   * @param taskId - The ID of the task the step belongs to
   * @param stepId - The ID of the step to delete
   * @returns A promise that resolves when the step is deleted
   */
  const deleteStep = async (taskId: string, stepId: string): Promise<void> => {
    //console.log(`deleteStep: Deleting step ${stepId} from task ${taskId}`)
    try {
      await axiosClient.delete(`/tasks/${taskId}/steps/${stepId}`)
      //console.log(`deleteStep: Step ${stepId} deleted successfully`)
    } catch (error) {
      console.error('Error deleting step:', error)
      throw error
    }
  }

  /**
   * Update only the status of a task
   * @param taskId - The ID of the task to update
   * @param status - The new status for the task
   * @returns A promise that resolves to the updated Task object
   */
  const updateTaskStatus = async (taskId: string, status: string): Promise<Task> => {
    //console.log(`updateTaskStatus: Updating status for task ${taskId} to ${status}`)
    try {
      const { data } = await axiosClient.put<Task>(`/tasks/${taskId}`, { status })
      //console.log('updateTaskStatus: Task status updated successfully', data)
      return data
    } catch (error) {
      console.error('Error updating task status:', error)
      throw error
    }
  }

  // Return an object with all the API functions
  return {
    fetchTasks,
    fetchTask,
    createTask,
    updateTask,
    deleteTask,
    getSteps,
    createStep,
    updateStep,
    deleteStep,
    updateTaskStatus,
  }
}

/*
* Schema

DTO for creating a Task.

name: string
description: string | null
status: string | null
priority: string | null
due_date: string | null
focus_at_date: string | null
focus_at: string | null
focus_block: string | null
duration: integer | null
position: integer | null
project_id: string uuid4

Notes
Notes:

focus_at_date is the local date the user intends to act on the task
focus_at is the local date & time the user intends to act on the task

Only one of focus_at or focus_block can be set at a time.


 *
 * Usage tips:
 * - Use 'cursor' and 'size' for pagination when fetching multiple tasks or steps
 * - Use 'sort' to order tasks by specific fields (e.g., 'position,-due_date')
 * - Use 'include' to fetch related data (like steps) along with tasks to reduce API calls
 * - Use date filters (focus_at_start, focus_at_end, due_date_start, due_date_end) to fetch tasks for specific time ranges
 * - Use 'status' to filter tasks by their completion status
 * - Use 'include_deleted=true' when you need to retrieve soft-deleted items (useful for data recovery or audit purposes)
 */

// Add this line at the end of the file
export type { TasksResponse }

